import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { can as canDo } from '../roles';

const AuthContext = createContext(null);
const VIEW_KEY = 'mouldos_view_as';

// When a sign-in runs out, read from the token itself (ms), or null when it
// cannot be read -- then the server's check decides.
function expiresAt(token) {
  try {
    const body = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(body)).exp;
    return exp ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [realUser, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);
  // View as (3 Nov 2026): a director seeing what a Setter or an Operator see. For
  // this visit only, so nobody opens the app tomorrow missing their buttons.
  const [viewAs, setViewAsNow] = useState(() => { try { return sessionStorage.getItem(VIEW_KEY) || null; } catch { return null; } });
  const realLevel = realUser?.permissionLevel || null;
  const viewing = realLevel === 'director' ? viewAs : null;
  // Every page reads this one: the role in view, as if signed in with it.
  const user = realUser && viewing ? { ...realUser, permissionLevel: viewing, role: viewing } : realUser;
  if (viewing) api.defaults.headers.common['X-View-As'] = viewing;
  else delete api.defaults.headers.common['X-View-As'];
  function setViewAs(level) {
    try { if (level) sessionStorage.setItem(VIEW_KEY, level); else sessionStorage.removeItem(VIEW_KEY); } catch { /* for this page only */ }
    setViewAsNow(level);
    // From a clean start: what the last role loaded stays out of this one.
    window.location.href = '/today';
  }

  // Opening the app: a stored sign-in holds until it runs out or the server
  // turns it down (the director, 3 Nov 2026: "keep me signed in for 30 days
  // doesn't work"). It used to wait on the server and show the sign-in page
  // when the server did not answer -- slow to wake, restarting after an
  // update, a phone between networks -- though the sign-in was still good.
  useEffect(() => {
    const token = localStorage.getItem('mouldos_token');
    const saved = localStorage.getItem('mouldos_user');
    if (token && saved) {
      const ends = expiresAt(token);
      if (ends && ends <= Date.now()) {
        localStorage.removeItem('mouldos_token');
        localStorage.removeItem('mouldos_user');
        sessionStorage.setItem('session_expired', '1');
      } else {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(JSON.parse(saved));
        // Checked behind the scenes: a 401 signs out through api.js; no
        // answer at all leaves the person signed in.
        api.get('/api/v1/auth/me').catch(() => {});
      }
    }
    setLoading(false);
  }, []);

  function login(token, userData) {
    try { sessionStorage.removeItem(VIEW_KEY); } catch { /* nothing kept */ }
    setViewAsNow(null);
    localStorage.setItem('mouldos_token', token);
    localStorage.setItem('mouldos_user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }

  function logout() {
    try { sessionStorage.removeItem(VIEW_KEY); } catch { /* nothing kept */ }
    localStorage.removeItem('mouldos_token');
    localStorage.removeItem('mouldos_user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }

  function isDirector() {
    return user?.permissionLevel === 'director';
  }

  function isSetter() {
    return user?.permissionLevel === 'setter' || isDirector();
  }

  // What this person may do (roles.js, the same list the server checks).
  function can(what) {
    return canDo(user, what);
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isDirector, isSetter, can, loading, viewAs: viewing, realLevel, setViewAs }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}